/* Sunset Performance — in-house YMM Garage (vanilla, no deps).
 * Year -> Make -> Model -> Submodel cascade with NO dead ends, driven by
 * assets/fitment_cascade.json. Persists the chosen vehicle in localStorage and
 * navigates to a fit_make_/fit_model_ tag-filtered collection on "Find Parts".
 * The fit_* tags are written to products by agent_bridge.sunset_ymm_tags.
 */
(function () {
  "use strict";
  var STORE_KEY = "sunset_vehicle_v2"; // Phase 0: bump ignores stale saves that deep-linked to old-scheme URLs
  var DATA = null;

  // Where the fitment cascade JSON lives. On a page WITH the garage section, init()
  // overwrites this with the section's canonical {{ 'fitment_cascade.json' | asset_url }}.
  // For a page WITHOUT the garage (so init never runs), derive it from THIS script's
  // own <src> — ymm-garage.js and fitment_cascade.json are siblings in /assets/ on the
  // same CDN host — so window.SunsetYMM can still lazy-load the data on demand.
  var cascadeUrl = (function () {
    try {
      var s = document.currentScript;
      if (s && s.src) return s.src.replace(/ymm-garage\.js(\?.*)?$/, "fitment_cascade.json");
    } catch (e) {}
    return null;
  })();

  function slug(s) {
    return (s || "").toString().trim().toLowerCase()
      .replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  }

  // Single-source vehicle label (year make model [submodel]); the garage form and the
  // public API both use this exact helper — no fork.
  function vehicleLabel(v) {
    if (!v) return "";
    return [v.year, v.make, v.model, (v.submodel && v.submodel !== "(all)") ? v.submodel : ""]
      .filter(Boolean).join(" ");
  }

  // Broadcast a same-tab vehicle change so the category rail + "Shop by category"
  // grid re-scope live (the 'storage' event only fires cross-tab, not same-tab).
  function notifyVehicleChange() {
    try { window.dispatchEvent(new CustomEvent("sunset:vehicle")); } catch (e) {}
  }

  // The saved vehicle from localStorage, or null. Shape: {year, make, model, submodel}.
  function savedVehicle() {
    var raw; try { raw = localStorage.getItem(STORE_KEY); } catch (e) { return null; }
    if (!raw) return null;
    var v; try { v = JSON.parse(raw); } catch (e) { return null; }
    if (!v || !v.year || !v.make) return null;
    return { year: v.year, make: v.make, model: v.model, submodel: v.submodel };
  }

  // Idempotent, memoized loader for the ~63KB cascade JSON. Returns a Promise that
  // resolves with DATA once loaded. Shared by the garage's own lazy-load AND by the
  // public window.SunsetYMM.ready, so both wait on the SAME single fetch.
  var _dataPromise = null;
  function ensureData() {
    if (_dataPromise) return _dataPromise;
    _dataPromise = new Promise(function (resolve, reject) {
      if (DATA) { resolve(DATA); return; }
      if (!cascadeUrl) { reject(new Error("SunsetYMM: no cascade URL")); return; }
      fetch(cascadeUrl).then(function (r) { return r.json(); }).then(function (json) {
        DATA = json; resolve(DATA);
      }).catch(function (err) {
        _dataPromise = null; // allow a retry on the next call
        reject(err);
      });
    });
    return _dataPromise;
  }

  // Port of meta.resolveKey_contract from fitment_cascade.json: rebuild the SAME fitv_
  // query key the writeset wrote, from a dropdown pick of DISPLAY names. Requires DATA
  // (genIndex / modelAlias / validKeys) to be loaded first; returns null if not loaded
  // or no candidate key exists (caller then routes to /search?q=<Year Make Model>).
  function subclassForGen(gen, submodel) {
    if (!gen || !gen.subclasses) return null;
    var sm = slug(submodel);
    var keys = Object.keys(gen.subclasses);
    for (var i = 0; i < keys.length; i++) {
      var k = slug(keys[i]);
      if (k === sm || k.indexOf(sm) !== -1 || sm.indexOf(k) !== -1) return gen.subclasses[keys[i]];
    }
    return null;
  }
  function resolveKey(v) {
    if (!DATA || !v) return null;
    var genIndex = DATA.genIndex || {}, modelAlias = DATA.modelAlias || {}, validKeys = DATA.validKeys || {};
    var mk = slug(v.make), md = slug(v.model);
    if (!mk || !md) return null;
    var alias = modelAlias[mk + "/" + md] || modelAlias[md] || null;
    var spineModel = alias ? alias.model : md;
    var forcedSub = alias ? alias.subclass : null;
    var year = parseInt(v.year, 10);
    var gens = genIndex[mk + "|" + spineModel] || [];
    var gen = null;
    for (var i = 0; i < gens.length; i++) {
      var g = gens[i];
      if (!isNaN(year) && g.y0 <= year && year <= g.y1) { gen = g; break; }
    }
    var sub = forcedSub || subclassForGen(gen, v.submodel);
    var base = "fitv_" + mk + "-" + spineModel;
    var cands = [];
    if (gen && gen.year_critical) cands.push(base + "-y" + v.year);
    if (gen && sub) cands.push(base + "-" + gen.slug + "-" + sub);
    if (gen) cands.push(base + "-" + gen.slug);
    if (sub) cands.push(base + "-" + sub);
    cands.push(base);
    var has = Object.prototype.hasOwnProperty;
    for (var j = 0; j < cands.length; j++) {
      if (has.call(validKeys, cands[j])) return cands[j];
    }
    return null;
  }
  function opt(value, label) {
    var o = document.createElement("option");
    o.value = value; o.textContent = label == null ? value : label;
    return o;
  }
  function reset(sel, placeholder) {
    sel.innerHTML = "";
    sel.appendChild(opt("", placeholder));
    sel.disabled = true;
  }

  function init(section) {
    var url = section.getAttribute("data-cascade-url");
    if (url) cascadeUrl = url; // section's asset_url is canonical; feed the shared loader
    var target = section.getAttribute("data-target-collection") || "all";
    var ySel = section.querySelector('[data-ymm="year"]');
    var mkSel = section.querySelector('[data-ymm="make"]');
    var mdSel = section.querySelector('[data-ymm="model"]');
    var subSel = section.querySelector('[data-ymm="submodel"]');
    var go = section.querySelector("[data-ymm-go]");
    var form = section.querySelector("[data-ymm-form]");
    var chip = section.querySelector("[data-ymm-chip]");
    var chipLabel = section.querySelector("[data-ymm-chip-label]");
    var clearBtn = section.querySelector("[data-ymm-clear]");
    var bar = section.querySelector("[data-ymm-bar]");
    var barLink = section.querySelector("[data-ymm-bar-link]");
    var barLabel = section.querySelector("[data-ymm-bar-label]");
    var barChange = section.querySelector("[data-ymm-bar-change]");
    var barClear = section.querySelector("[data-ymm-bar-clear]");

    // Build the fit-tag for a vehicle (same rule used on submit).
    function fitTag(v) {
      return v.model
        ? "fit_model_" + slug(v.make) + "-" + slug(v.model)
        : "fit_make_" + slug(v.make);
    }
    // Build the destination collection URL using the CLEAN generation-precise
    // fitv_ key (same resolveKey the Shop-by-category tiles use). Honors the current
    // collection context so a saved vehicle deep-links within the browsed category.
    // If the vehicle can't resolve to a live fitv_ key, fall back to keyword search
    // rather than an old fit_model_ collection (never surface a wrong/corrupt set).
    function fitUrl(v) {
      var key = resolveKey(v);
      if (!key) return "/search?q=" + encodeURIComponent([v.year, v.make, v.model].filter(Boolean).join(" ").trim());
      var handle = target;
      var m = window.location.pathname.match(/^\/collections\/([^\/]+)/);
      if (m && m[1] && m[1] !== "all") handle = decodeURIComponent(m[1]);
      return "/collections/" + encodeURIComponent(handle) + "/" + encodeURIComponent(key);
    }
    // Phase 0: pick the navigation destination. A model-level pick uses the live
    // fit_model_ collection. A make-ONLY pick (no model) goes to keyword search
    // instead of a whole-make collection, which surfaced parts for the entire make
    // regardless of the shopper's actual model.
    function destination(v) {
      if (v.model) return fitUrl(v);
      return "/search?q=" + encodeURIComponent([v.year, v.make].filter(Boolean).join(" "));
    }
    function years() {
      return Object.keys(DATA.makes_by_year).map(Number).sort(function (a, b) { return b - a; });
    }
    function makesForYear(y) {
      return (DATA.makes_by_year[String(y)] || []).slice().sort();
    }
    function modelsFor(y, mk) {
      var tree = DATA.tree[mk] || {};
      return Object.keys(tree).filter(function (md) {
        return Object.keys(tree[md]).some(function (sub) { return tree[md][sub].indexOf(y) !== -1; });
      }).sort();
    }
    function submodelsFor(y, mk, md) {
      var node = (DATA.tree[mk] || {})[md] || {};
      return Object.keys(node).filter(function (sub) { return node[sub].indexOf(y) !== -1; }).sort();
    }

    function refreshGo() {
      go.disabled = !(ySel.value && mkSel.value); // year+make minimum; model/submodel narrow further
    }

    function fillYears() {
      reset(ySel, "Year"); ySel.disabled = false;
      years().forEach(function (y) { ySel.appendChild(opt(String(y))); });
    }
    function onYear() {
      reset(mkSel, "Make"); reset(mdSel, "Model"); reset(subSel, "Submodel");
      if (ySel.value) {
        mkSel.disabled = false;
        makesForYear(Number(ySel.value)).forEach(function (mk) { mkSel.appendChild(opt(mk)); });
      }
      refreshGo();
    }
    function onMake() {
      reset(mdSel, "Model"); reset(subSel, "Submodel");
      if (ySel.value && mkSel.value) {
        mdSel.disabled = false;
        modelsFor(Number(ySel.value), mkSel.value).forEach(function (md) { mdSel.appendChild(opt(md)); });
      }
      refreshGo();
    }
    function onModel() {
      reset(subSel, "Submodel");
      if (ySel.value && mkSel.value && mdSel.value) {
        var subs = submodelsFor(Number(ySel.value), mkSel.value, mdSel.value);
        if (subs.length && !(subs.length === 1 && subs[0] === "(all)")) {
          subSel.disabled = false;
          subs.forEach(function (s) { subSel.appendChild(opt(s, s === "(all)" ? "All submodels" : s)); });
        }
      }
      refreshGo();
    }

    function saveVehicle() {
      var v = { year: ySel.value, make: mkSel.value, model: mdSel.value, submodel: subSel.value, schema: 2 };
      // Phase 0: persist ONLY the vehicle, never a resolved tag/URL. The destination
      // is always recomputed through destination() at click time, so a future tag-scheme
      // change can't leave a stale deep-link behind.
      try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) {}
      renderChip(v); renderBar(v); notifyVehicleChange();
    }
    function renderChip(v) {
      if (!v || !v.year || !v.make) { chip.hidden = true; return; }
      chipLabel.textContent = vehicleLabel(v);
      chip.hidden = false;
    }
    function renderBar(v) {
      if (!bar) return;
      if (!v || !v.year || !v.make) { bar.hidden = true; return; }
      barLabel.textContent = vehicleLabel(v);
      // Phase 0: always recompute the destination (honors current collection context
      // and the make-only search fallback); never trust a stored URL.
      barLink.setAttribute("href", destination(v));
      bar.hidden = false;
    }
    function hideBars() {
      chip.hidden = true;
      if (bar) bar.hidden = true;
    }
    function restore() {
      var raw; try { raw = localStorage.getItem(STORE_KEY); } catch (e) { raw = null; }
      if (!raw) return;
      var v; try { v = JSON.parse(raw); } catch (e) { return; }
      if (!v || !v.year || !v.make) return;
      // replay the cascade so dropdowns reflect the saved vehicle
      ySel.value = String(v.year); onYear();
      if ([].slice.call(mkSel.options).some(function (o) { return o.value === v.make; })) {
        mkSel.value = v.make; onMake();
        if (v.model && [].slice.call(mdSel.options).some(function (o) { return o.value === v.model; })) {
          mdSel.value = v.model; onModel();
          if (v.submodel) { subSel.value = v.submodel; }
        }
      }
      refreshGo(); renderChip(v); renderBar(v);
    }

    function clearVehicle() {
      try { localStorage.removeItem(STORE_KEY); } catch (e) {}
      fillYears(); reset(mkSel, "Make"); reset(mdSel, "Model"); reset(subSel, "Submodel");
      refreshGo(); hideBars(); notifyVehicleChange();
    }
    function focusGarage() {
      // Reopen/repopulate the garage: scroll the (already pre-filled) form into
      // view and focus the first empty cascade step.
      form.scrollIntoView({ behavior: "smooth", block: "center" });
      var firstOpen = [ySel, mkSel, mdSel, subSel].filter(function (s) { return !s.disabled; });
      var pick = firstOpen.length ? firstOpen[0] : ySel;
      try { pick.focus(); } catch (e) {}
    }

    ySel.addEventListener("change", onYear);
    mkSel.addEventListener("change", onMake);
    mdSel.addEventListener("change", onModel);
    subSel.addEventListener("change", refreshGo);
    clearBtn.addEventListener("click", clearVehicle);
    if (barClear) barClear.addEventListener("click", clearVehicle);
    if (barChange) barChange.addEventListener("click", function (e) { e.preventDefault(); focusGarage(); });

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (!ySel.value || !mkSel.value) return;
      saveVehicle(); // persists vehicle + broadcasts the change (rail/grid re-scope)
      var v = { year: ySel.value, make: mkSel.value, model: mdSel.value };
      // Vehicle + INTENT, not a firehose: if a vehicle-aware "Shop by category"
      // grid is on THIS page and we're not already inside a specific category,
      // scroll the shopper to the (now-scoped) category tiles so they pick what
      // they're after — instead of dumping every part that fits the car.
      var m = window.location.pathname.match(/^\/collections\/([^\/]+)/);
      var onSpecificCollection = m && m[1] && m[1] !== "all";
      var grid = document.querySelector("[data-sgc-section]");
      if (grid && !onSpecificCollection) {
        if (grid.scrollIntoView) grid.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      // Otherwise navigate. This store filters via the URL PATH
      // /collections/<handle>/<tag> (NOT the ?filter.p.tag= facet, which is dead
      // here). fitUrl() honors the current collection context so the shopper gets
      // "<category> for <their car>"; off-collection falls back to "all".
      window.location.href = destination(v);
    });

    // Render the persistent bar IMMEDIATELY from localStorage — it does not depend on
    // the cascade JSON, so the "Shopping for" bar shows on every page even before (or if)
    // the fitment data finishes loading.
    (function showSavedBarEarly() {
      var raw; try { raw = localStorage.getItem(STORE_KEY); } catch (e) { raw = null; }
      if (!raw) return;
      var v; try { v = JSON.parse(raw); } catch (e) { return; }
      if (v && v.year && v.make) renderBar(v);
    })();

    // Performance: defer the ~63KB fitment_cascade.json fetch so it stops competing
    // with the LCP hero image on a cold mobile load. The persistent "Shopping for"
    // bar already rendered above from localStorage and does NOT need this data.
    // The cascade loads lazily on first interaction with the garage (focus/pointer/
    // touch), with an idle-time fallback so the dropdowns still populate for a user
    // who just sits there. loadCascade() is idempotent.
    var cascadeStarted = false;
    function loadCascade() {
      if (cascadeStarted) return;
      cascadeStarted = true;
      removeLazyTriggers();
      ensureData().then(function () {
        fillYears();
        restore();
      }).catch(function (err) {
        cascadeStarted = false; // allow a retry on next interaction
        var note = section.querySelector("[data-ymm-note]");
        if (note) note.textContent = "Vehicle lookup is loading… (" + err + ")";
      });
    }

    var LAZY_EVENTS = ["focusin", "pointerdown", "touchstart", "mouseover"];
    function onLazyTrigger() { loadCascade(); }
    function removeLazyTriggers() {
      LAZY_EVENTS.forEach(function (ev) {
        section.removeEventListener(ev, onLazyTrigger, true);
      });
    }
    LAZY_EVENTS.forEach(function (ev) {
      section.addEventListener(ev, onLazyTrigger, { capture: true, passive: true });
    });

    // Idle fallback: if the visitor never touches the garage, load the cascade once
    // the main thread is idle (or after a short delay) so the dropdowns are ready
    // and a saved vehicle can be replayed — well after the LCP window has closed.
    if ("requestIdleCallback" in window) {
      requestIdleCallback(function () { loadCascade(); }, { timeout: 6000 });
    } else {
      setTimeout(loadCascade, 4000);
    }
  }

  function boot() {
    document.querySelectorAll("[data-ymm-section]").forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else { boot(); }

  /* ------------------------------------------------------------------ *
   * Public helper API — window.SunsetYMM
   * Lets OTHER sections (e.g. "Shop by category") scope their links to the
   * saved vehicle without duplicating cascade logic. resolveKey() is the SAME
   * module-scope function the garage uses (single source; not a fork), and
   * `ready` waits on the SAME shared ensureData() fetch. `ready` is a getter so
   * merely defining the API does NOT eager-load the ~63KB JSON — the fetch is
   * triggered only when a consumer actually reads .ready (or the garage lazy-
   * loads it first), preserving the LCP-friendly deferral. On a page WITHOUT the
   * garage section, reading .ready still triggers the load via the script-derived
   * cascadeUrl, so the API works standalone.
   *   ready          : Promise<DATA>  (resolves once the cascade JSON is loaded)
   *   resolveKey(v)  : fitvKey|null   (v = {year,make,model,submodel} display names)
   *   savedVehicle() : {year,make,model,submodel}|null  (from localStorage)
   *   fitKeyForSaved(): fitvKey|null  (resolveKey of the saved vehicle)
   *   vehicleLabel(v): string         ("Year Make Model [Submodel]")
   * ------------------------------------------------------------------ */
  var SunsetYMM = {
    resolveKey: function (v) { return resolveKey(v); },
    savedVehicle: function () { return savedVehicle(); },
    fitKeyForSaved: function () { var v = savedVehicle(); return v ? resolveKey(v) : null; },
    vehicleLabel: function (v) { return vehicleLabel(v); }
  };
  // Lazy getter: first read triggers the shared fetch; subsequent reads return the
  // same memoized promise. Resolves with DATA; consumers typically:
  //   SunsetYMM.ready.then(function(){ var k = SunsetYMM.fitKeyForSaved(); ... });
  try {
    Object.defineProperty(SunsetYMM, "ready", {
      enumerable: true, configurable: true,
      get: function () { return ensureData(); }
    });
  } catch (e) {
    SunsetYMM.ready = ensureData(); // ancient-browser fallback (eager, but functional)
  }
  window.SunsetYMM = SunsetYMM;

  /* ------------------------------------------------------------------ *
   * Search <-> Garage bridge (Phase-0 interim).
   * Scopes the keyword search to the shopper's vehicle via Shopify's
   * filter.p.tag, using the fit_model_ tags already on products — so
   * "coilovers" narrows from ~1000 to the ~37 that actually fit.
   * Two sources, typed query wins over the garaged vehicle:
   *   (1) a vehicle name typed in the query (e.g. "gt86 coilovers")
   *   (2) the vehicle saved in the garage
   * Only the result set narrows; the facet UI stays hidden (show_filter=false),
   * so the search page looks unchanged. Uses the SAME slug() + STORE_KEY as the
   * garage. This is interim: it inherits the current tags' limits (GT86/GR86
   * still share, universals can slip in) until the full rebuild.
   * ------------------------------------------------------------------ */
  // Common performance-vehicle names -> a fit_model_ tag VERIFIED to exist live.
  // (STI has no own tag -> WRX, its platform. Extend as the rebuild lands.)
  var VEH_ALIAS = {
    "gt86": "toyota-86", "gt-86": "toyota-86", "86": "toyota-86",
    "gr86": "toyota-gr86", "gr-86": "toyota-gr86",
    "frs": "scion-fr-s", "fr-s": "scion-fr-s",
    "brz": "subaru-brz",
    "wrx": "subaru-wrx", "sti": "subaru-wrx",
    "supra": "toyota-supra",
    "350z": "nissan-350z", "370z": "nissan-370z",
    "gtr": "nissan-gt-r", "gt-r": "nissan-gt-r", "r35": "nissan-gt-r",
    "civic": "honda-civic", "s2000": "honda-s2000", "s2k": "honda-s2000",
    "mustang": "ford-mustang",
    "camaro": "chevrolet-camaro",
    "corvette": "chevrolet-corvette", "vette": "chevrolet-corvette",
    "miata": "mazda-mx-5-miata", "mx5": "mazda-mx-5-miata", "mx-5": "mazda-mx-5-miata",
    "golf-r": "volkswagen-golf-r", "golfr": "volkswagen-golf-r"
  };
  function savedFitTag() {
    var raw; try { raw = localStorage.getItem(STORE_KEY); } catch (e) { return null; }
    if (!raw) return null;
    var v; try { v = JSON.parse(raw); } catch (e) { return null; }
    if (!v || !v.make || !v.model) return null; // need a model for a fit_model_ tag
    return "fit_model_" + slug(v.make) + "-" + slug(v.model);
  }
  function typedScope(q) {
    if (!q) return null;
    var norm = " " + q.toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim() + " ";
    var keys = Object.keys(VEH_ALIAS).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) {
      var phrase = " " + keys[i] + " ";
      if (norm.indexOf(phrase) !== -1) {
        var rest = norm.split(phrase).join(" ").replace(/\s+/g, " ").trim();
        return { tag: "fit_model_" + VEH_ALIAS[keys[i]], rest: rest };
      }
    }
    return null;
  }

  /* Reliable search scoping (Phase 2). A "vehicle + category" query (e.g.
   * "gt86 coilovers") routes to the SAME /collections/<handle>/<fitvKey> path
   * the garage + category tiles use — verified to narrow correctly (GT86
   * coilovers: 13, not 1000) — instead of the unreliable ?filter.p.tag= facet.
   * CATEGORY_TERMS mirrors the homepage "Performance Starts Here" categories;
   * longest phrases first so "cold air"/"air suspension"/"lowering spring" win
   * over their shorter substrings. */
  var CATEGORY_TERMS = [
    ["air suspension", "air-suspension"], ["air ride", "air-suspension"],
    ["lowering spring", "coilovers-springs"], ["cold air", "air-intakes-filters"],
    ["air filter", "air-intakes-filters"], ["throttle body", "air-intakes-filters"],
    ["blow off", "forced-induction-1"], ["blow-off", "forced-induction-1"],
    ["charge pipe", "forced-induction-1"], ["boost controller", "forced-induction-1"],
    ["sway bar", "suspension-steering"], ["control arm", "suspension-steering"],
    ["tie rod", "suspension-steering"], ["connecting rod", "engine-internals"],
    ["valve spring", "engine-internals"], ["head gasket", "engine-internals"],
    ["brake pad", "brakes"], ["fuel pump", "fuel"], ["fuel injector", "fuel"],
    ["oil cooler", "cooling-1"],
    ["coilover", "coilovers-springs"], ["coil over", "coilovers-springs"],
    ["spring", "coilovers-springs"], ["catback", "exhaust-components"],
    ["cat-back", "exhaust-components"], ["axleback", "exhaust-components"],
    ["axle-back", "exhaust-components"], ["downpipe", "exhaust-components"],
    ["header", "exhaust-components"], ["muffler", "exhaust-components"],
    ["exhaust", "exhaust-components"], ["intake", "air-intakes-filters"],
    ["manifold", "air-intakes-filters"], ["filter", "air-intakes-filters"],
    ["supercharger", "forced-induction-1"], ["turbo", "forced-induction-1"],
    ["wastegate", "forced-induction-1"], ["intercooler", "forced-induction-1"],
    ["boost", "forced-induction-1"], ["bov", "forced-induction-1"],
    ["rotor", "brakes"], ["caliper", "brakes"], ["brake", "brakes"],
    ["clutch", "clutch-flywheel"], ["flywheel", "clutch-flywheel"],
    ["radiator", "cooling-1"], ["coolant", "cooling-1"], ["cooling", "cooling-1"],
    ["injector", "fuel"], ["fuel", "fuel"],
    ["strut", "suspension-steering"], ["suspension", "suspension-steering"],
    ["steering", "suspension-steering"], ["piston", "engine-internals"],
    ["camshaft", "engine-internals"], ["engine", "engine-internals"],
    ["pulley", "accesory-drive"], ["tensioner", "accesory-drive"], ["belt", "accesory-drive"]
  ];
  function categoryFor(q) {
    if (!q) return null;
    var s = " " + q.toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim() + " ";
    for (var i = 0; i < CATEGORY_TERMS.length; i++) {
      // normalize the term the SAME way as the query (hyphens -> spaces) so
      // "cat-back"/"blow-off" match, and tolerate a trailing plural 's'.
      var t = CATEGORY_TERMS[i][0].replace(/[^a-z0-9]+/g, " ");
      if (s.indexOf(" " + t + " ") !== -1 || s.indexOf(" " + t + "s ") !== -1) return CATEGORY_TERMS[i][1];
    }
    return null;
  }

  /* Typed vehicle name (no year) -> a SINGLE fitv_ key that is populated AND
   * unambiguous. ONLY single-generation platforms qualify: their one gen key
   * IS the whole model and carries all the products. Multi-gen platforms (BRZ
   * zn6/zn8, Camaro, Golf-R, WRX, Mustang, Corvette, Civic, Supra, GT-R) are
   * omitted on purpose — their gen-less "base" key holds only a handful of
   * products (BRZ base=4, Camaro base=1, 350Z base=2), so a typed-without-year
   * search can't pin them to one populated key. Those fall through to keyword
   * search; GARAGING the vehicle resolves the exact gen key and scopes cleanly. */
  var VEH_FITKEY = {
    // single-gen platforms -> their one populated gen key
    "gt86": "fitv_toyota-86-zn6", "gt-86": "fitv_toyota-86-zn6", "86": "fitv_toyota-86-zn6",
    "gr86": "fitv_toyota-gr86-zn8", "gr-86": "fitv_toyota-gr86-zn8",
    "frs": "fitv_scion-fr-s-zn6", "fr-s": "fitv_scion-fr-s-zn6",
    "350z": "fitv_nissan-350z-z33", "370z": "fitv_nissan-370z-z34",
    // multi-gen platforms -> model-level BASE key (backed by the base-tag write:
    // sunset_base_tag_writer). Typed-without-year = all generations of the model.
    "brz": "fitv_subaru-brz",
    "wrx": "fitv_subaru-wrx", "sti": "fitv_subaru-wrx",
    "supra": "fitv_toyota-supra",
    "gtr": "fitv_nissan-gt-r", "gt-r": "fitv_nissan-gt-r", "r35": "fitv_nissan-gt-r",
    "civic": "fitv_honda-civic",
    "mustang": "fitv_ford-mustang",
    "camaro": "fitv_chevrolet-camaro",
    "corvette": "fitv_chevrolet-corvette", "vette": "fitv_chevrolet-corvette",
    "miata": "fitv_mazda-mx-5-miata", "mx5": "fitv_mazda-mx-5-miata", "mx-5": "fitv_mazda-mx-5-miata",
    "golf-r": "fitv_volkswagen-golf-r", "golfr": "fitv_volkswagen-golf-r"
  };
  function typedFitvKey(q) {
    if (!q) return null;
    var s = " " + q.toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim() + " ";
    var keys = Object.keys(VEH_FITKEY).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) {
      // normalize the key like the query so hyphenated forms (fr-s, gt-r, mx-5,
      // golf-r) match too — the query turns every hyphen into a space.
      var kn = keys[i].replace(/[^a-z0-9]+/g, " ");
      if (s.indexOf(" " + kn + " ") !== -1) return VEH_FITKEY[keys[i]];
    }
    return null;
  }

  // Delegated + capture so it runs before the theme's own submit handler; the form
  // then submits natively (GET) with our hidden filter input included.
  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form || form.tagName !== "FORM") return;
    var isSearch = (form.getAttribute("action") || "").toLowerCase().indexOf("/search") !== -1;
    // The facet sidebar form on the search-results page: re-assert the saved vehicle
    // so clicking a category/brand/price filter never drops the shopper's car.
    var isFacet = form.id === "CollectionFiltersForm" && location.pathname.toLowerCase().indexOf("/search") !== -1;
    if (!isSearch && !isFacet) return;
    var tag = null;
    if (isSearch) {
      var qi = form.querySelector('input[name="q"]');
      if (!qi) return;

      // Reliable path: query names a CATEGORY and we can pin the vehicle to ONE
      // fitv_ key -> go to /collections/<handle>/<fitvKey> (proven to narrow),
      // instead of the unreliable ?filter.p.tag= facet below.
      var catHandle = categoryFor(qi.value);
      if (catHandle) {
        var typedKey = typedFitvKey(qi.value);       // typed vehicle, sync, no cascade
        if (typedKey) {
          e.preventDefault();
          window.location.href = "/collections/" + catHandle + "/" + typedKey;
          return;
        }
        var Y = window.SunsetYMM, sv = null;
        try { sv = Y && Y.savedVehicle(); } catch (e2) {}
        if (sv) {
          var sk = null; try { sk = Y.fitKeyForSaved(); } catch (e2) {}
          if (sk) {
            e.preventDefault();
            window.location.href = "/collections/" + catHandle + "/" + sk;
            return;
          }
          // cascade not loaded yet -> defer the nav until it resolves the key
          e.preventDefault();
          var qsafe = qi.value;
          Promise.resolve(Y.ready).then(function () {
            var k2 = null; try { k2 = Y.fitKeyForSaved(); } catch (e3) {}
            window.location.href = k2 ? ("/collections/" + catHandle + "/" + k2)
                                      : ("/search?q=" + encodeURIComponent(qsafe));
          }).catch(function () { window.location.href = "/search?q=" + encodeURIComponent(qsafe); });
          return;
        }
      }

      var typed = typedScope(qi.value);
      if (typed) {
        if (typed.rest) qi.value = typed.rest; // drop the vehicle word so the filter can narrow
        tag = typed.tag;
      } else {
        tag = savedFitTag();
      }
    } else {
      tag = savedFitTag(); // facet form: keep the vehicle scope
    }
    if (!tag) return;
    var inp = form.querySelector('input[name="filter.p.tag"]');
    if (!inp) {
      inp = document.createElement("input");
      inp.type = "hidden"; inp.name = "filter.p.tag";
      form.appendChild(inp);
    }
    inp.value = tag;
  }, true);

  /* On the results page, show a "Fits <Vehicle>" chip so a scoped search never
   * reads as if it dropped the car (e.g. heading "124 results for 'brakes'").
   * Prefers the garaged vehicle's full label (with year) when it matches the
   * applied tag; else derives a name from the tag itself. NOTE: the underlying
   * tag is model-level only, so results are NOT year-filtered yet — that lands
   * with the rebuild. The chip intentionally says "(all years)" to stay honest. */
  function tagToName(tag) {
    var body = tag.replace(/^fit_model_/, "").replace(/^fit_make_/, "");
    return body.split("-").map(function (w) {
      return w ? w.charAt(0).toUpperCase() + w.slice(1) : w;
    }).join(" ");
  }
  /* Universal search scoping: however a "vehicle + category" search was fired —
   * the HEADER predictive search, the results-page form, or a bookmarked URL —
   * it lands on /search?q=... Reroute it here to the reliable scoped collection
   * so "GT86 coilovers" shows the 13 that fit, not 1000. Typed vehicles resolve
   * synchronously (no cascade needed); a garaged-only vehicle waits for it.
   * location.replace() so Back doesn't return to the unscoped results page. */
  (function scopedSearchRedirect() {
    if (location.pathname.toLowerCase().indexOf("/search") === -1) return;
    var q; try { q = new URLSearchParams(location.search).get("q"); } catch (e) { return; }
    if (!q) return;
    var cat = categoryFor(q);
    if (!cat) return;                       // not a category term -> keep native results
    var tk = typedFitvKey(q);
    if (tk) { location.replace("/collections/" + cat + "/" + tk); return; }
    var Y = window.SunsetYMM, sv = null;
    try { sv = Y && Y.savedVehicle(); } catch (e) {}
    if (!sv) return;                        // no vehicle to scope by -> keep native results
    Promise.resolve(Y.ready).then(function () {
      var k = null; try { k = Y.fitKeyForSaved(); } catch (e) {}
      if (k) location.replace("/collections/" + cat + "/" + k);
    }).catch(function () {});
  })();

  (function showScopeChip() {
    if (location.pathname.indexOf("/search") === -1) return;
    var params = new URLSearchParams(location.search);
    var tag = params.get("filter.p.tag");
    if (!tag || tag.indexOf("fit_") !== 0) return;
    var name = null, exactYears = false;
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (raw) {
        var v = JSON.parse(raw);
        if (v && v.make && v.model &&
            ("fit_model_" + slug(v.make) + "-" + slug(v.model)) === tag) {
          name = [v.make, v.model].join(" "); // model-level tag: no year filtering yet
        }
      }
    } catch (e) {}
    if (!name) name = tagToName(tag);
    function inject() {
      var h = document.querySelector(".m-page-header__title");
      if (!h || document.querySelector("[data-ymm-scope-chip]")) return;
      var chip = document.createElement("div");
      chip.setAttribute("data-ymm-scope-chip", "");
      chip.style.cssText = "display:inline-flex;align-items:center;gap:.4em;margin:.5em auto 0;" +
        "padding:.35em .8em;border-radius:999px;background:rgba(0,0,0,.06);" +
        "font-size:.85em;font-weight:600;line-height:1.2;";
      chip.innerHTML = '<span aria-hidden="true">🔧</span><span>Fits ' +
        name.replace(/</g, "&lt;") + ' <span style="font-weight:400;opacity:.7">(all years)</span></span>';
      h.parentNode.insertBefore(chip, h.nextSibling);
    }
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", inject);
    else inject();
  })();
})();
